import axios from 'axios';
import { getAuthToken } from '@redpanda/utils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5006/api';

/**
 * 預約後端根網址（不含 /api）：全系統只讀 REACT_APP_API_URL 一個變數。
 * 以前另有 REACT_APP_BOOKING_BACKEND_URL，但 Vercel 沒設，正式環境顧客登入／我的預約／圖片上傳會連到 localhost
 */
export const BOOKING_BACKEND_URL = API_URL.replace(/\/api\/?$/, '');
const IDENTITY_API_URL = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000/api';
const SUBSCRIPTION_API_URL = process.env.REACT_APP_SUBSCRIPTION_SERVICE_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// 添加請求攔截器自動設定 Authorization header
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const identityApi = axios.create({
  baseURL: IDENTITY_API_URL,
  withCredentials: true,
});

identityApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const subscriptionApi = axios.create({
  baseURL: SUBSCRIPTION_API_URL,
  withCredentials: true,
});

subscriptionApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Booking Limit APIs (using subscription-service)
export const getBookingLimitInfo = () => {
  return subscriptionApi.get('/subscriptions/booking-limit');
};

// Store APIs
export const storeApi = {
  get: () => api.get('/stores'), // 獲取當前認證用戶的店鋪（管理後台使用）
  getById: (storeId: string) => api.get(`/stores/${storeId}`), // 獲取指定店鋪公開資訊（客戶端使用）
  create: (data: any) => api.post('/stores', data),
  update: (id: string, data: any) => api.patch(`/stores/${id}`, data),
  getOperatingHours: (storeId: string) => api.get(`/stores/${storeId}/operating-hours`),
  updateOperatingHours: (storeId: string, data: any) => api.patch(`/stores/${storeId}/operating-hours`, data),
};

// Service APIs
export const serviceApi = {
  getByStore: (storeId: string) => api.get(`/stores/${storeId}/services`),
  create: (storeId: string, data: any) => api.post(`/stores/${storeId}/services`, data),
  update: (id: string, data: any) => api.patch(`/services/${id}`, data),
  delete: (id: string) => api.delete(`/services/${id}`),
  toggleStatus: (id: string, isActive: boolean) => api.patch(`/services/${id}/status`, { isActive }),
};

// Staff APIs
export const staffApi = {
  getByStore: (storeId: string) => api.get(`/stores/${storeId}/staff`),
  create: (storeId: string, data: any) => api.post(`/stores/${storeId}/staff`, data),
  update: (id: string, data: any) => api.patch(`/staff/${id}`, data),
  delete: (id: string) => api.delete(`/staff/${id}`),
  toggleStatus: (id: string, isActive: boolean) => api.patch(`/staff/${id}/status`, { isActive }),
};

// Booking APIs
export const bookingApi = {
  list: (filters?: any) => api.get('/bookings', { params: filters }),
  get: (id: string) => api.get(`/bookings/${id}`),
  getByToken: (token: string) => api.get(`/bookings/token/${token}`),
  getAvailableSlots: (params: { storeId: string; serviceId: string; date: string; staffId?: string }) =>
    api.get('/bookings/available-slots', { params }),
  create: (data: any) => api.post('/bookings', data),
  update: (id: string, data: any) => api.patch(`/bookings/${id}`, data),
  cancel: (id: string) => api.delete(`/bookings/${id}`),
  // 顧客自助改期、取消（不用登入，用預約管理 token 驗證；店家後台用上面兩支）
  rescheduleByToken: (token: string, data: { date: string; serviceId?: string; staffId?: string }) =>
    api.patch(`/bookings/token/${encodeURIComponent(token)}/reschedule`, data),
  cancelByToken: (token: string) => api.delete(`/bookings/token/${encodeURIComponent(token)}`),
  delete: (id: string) => api.post(`/bookings/${id}/delete`),
  // 分頁版本API (用於新的預約管理頁面)
  getPaginated: (page: number, pageSize: number, filters?: any) =>
    api.get('/bookings', { params: { ...filters, page, pageSize } }),
  // 批量刪除API
  bulkDelete: (ids: string[]) => api.post('/bookings/bulk-delete', { ids }),
};

// Customer APIs (B2B - for admin, 需要認證)
export const customerApi = {
  list: (filters?: any) => api.get('/customers', { params: filters }), // 後端從認證自動獲取 storeId
  getPaginated: (page: number, pageSize: number = 20, filters?: any) =>
    api.get('/customers', { params: { page, pageSize, ...filters } }),
  get: (id: string) => api.get(`/customers/${id}`), // 移除 storeId 參數
  update: (id: string, data: any) => api.patch(`/customers/${id}`, data), // 移除 storeId 參數
};

// Customer Auth APIs (C-end)
export const customerAuthApi = {
  register: (data: any) => api.post('/customers/register', data),
  login: (data: any) => api.post('/customers/login', data),
  forgotPassword: (email: string, storeId?: string, language?: string) => api.post('/customers/forgot-password', { email, storeId, language }),
  resetPassword: (token: string, newPassword: string) => api.post('/customers/reset-password', { token, newPassword }),
};

// Analytics APIs (需要認證)
export const analyticsApi = {
  bookings: () => api.get('/analytics/bookings'), // 後端從認證自動獲取 storeId，移除 filters
  revenue: () => api.get('/analytics/revenue'), // 後端從認證自動獲取 storeId，移除 filters
  customers: () => api.get('/analytics/customers'), // 後端從認證自動獲取 storeId，移除 filters
};

// LINE APIs（需認證）
export const lineApi = {
  testPush: (lineUserId: string) => api.post('/line/test-push', { lineUserId }),
  verifyToken: (channelAccessToken: string) => api.post('/line/verify-token', { channelAccessToken }),
};

// LINE Public APIs（不需認證）
export const linePublicApi = {
  getLoginUrl: (storeId: string) => api.get(`/line/auth/url/${storeId}`),
};

export default api;
